import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Settings, History, LogOut, Zap, Power, AlertOctagon, Book, ShieldAlert, 
  CreditCard, BarChart3, Menu, X, SlidersHorizontal, Shield, Heart 
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 

import { useTrading } from '../hooks/useTrading';
import { Dashboard } from './Dashboard';
import { ProfilePage } from '../pages/ProfilePage';
import { TradeHistoryTable } from './TradeHistoryTable';
import { ApiSettings } from './ApiSettings';
import { RightSidebar } from './RightSidebar'; 
import { UserGuide } from './UserGuide'; 
import { AdminPanel } from './AdminPanel'; 
import { SubscriptionPanel } from './SubscriptionPanel'; 
import api from '../api/api'; 

export const Platform = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(); 
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedSymbol, setSelectedSymbol] = useState('BTC/USDT');

  // Initialize Strategy Mode State
  const [strategyMode, setStrategyMode] = useState<'AUTO' | 'TREND' | 'SCALP'>('AUTO');

  const [isLeftOpen, setIsLeftOpen] = useState(false);
  const [isRightOpen, setIsRightOpen] = useState(false);

  // Hook to load user data (ВКЛЮЧАЯ ЛОГИ)
  const trading = useTrading(true);
  const userData = (trading as any).userData;
  const isAdmin = userData?.role === 'ADMIN';

  // ✅ FIX: Sync Strategy Mode with Backend on Load
  useEffect(() => {
    if (userData && userData.tradingMode) {
        setStrategyMode(userData.tradingMode as 'AUTO' | 'TREND' | 'SCALP');
    }
  }, [userData]); 

  useEffect(() => {
    setIsLeftOpen(false);
    setIsRightOpen(false);
  }, [activeTab, location]);

  // ✅ Update Strategy Mode and Save to Server
  const handleStrategyChange = async (mode: 'AUTO' | 'TREND' | 'SCALP') => {
    try {
      setStrategyMode(mode);
      await api.post('/users/settings', { tradingMode: mode });
      trading.addLog(`🔄 Strategy changed to: ${mode} MODE`);
    } catch (e) {
      console.error(e);
      alert("Error changing strategy");
    }
  };

  const togglePaperMode = async () => {
    try {
      const newStatus = !trading.isPaperMode;
      await api.post('/trading/toggle-paper', { status: newStatus });
      trading.setIsPaperMode(newStatus);
      trading.addLog(newStatus ? "🛡️ Mode: Paper Trading" : "⚠️ Mode: Real Trading");
    } catch (e) { alert("Error changing mode"); }
  };

  const toggleBot = async () => {
    try {
      const newStatus = !trading.isBotRunning;
      await api.post('/trading/toggle', { status: newStatus });
      trading.setIsBotRunning(newStatus);
      trading.addLog(newStatus ? "🤖 Bot Started" : "🛑 Bot Stopped");
    } catch (e) { alert("Error toggling bot"); }
  };

  const handlePanic = async () => {
    if (window.confirm(t('platform.actions.panicConfirm'))) {
      try {
        await api.post('/trading/panic-sell');
        trading.setIsBotRunning(false);
        trading.addLog("🛑 PANIC SELL EXECUTED");
      } catch (e) { alert("Panic Sell Error"); }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-[#0f172a] text-slate-200 font-sans overflow-hidden relative">
      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

      {/* === MOBILE HEADER === */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-[#1e293b] border-b border-slate-800 z-40 sticky top-0 shadow-lg">
         <div className="flex items-center gap-3">
             <button onClick={() => setIsLeftOpen(true)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg active:scale-95 transition-transform">
                <Menu size={24} />
             </button>
             <span className="font-bold text-white">TradingBot Pro</span>
         </div>

         <div className="flex items-center gap-3">
             {activeTab === 'dashboard' && (
                 <button onClick={() => setIsRightOpen(true)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg active:scale-95 transition-transform relative">
                    <SlidersHorizontal size={24} />
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-green-500 rounded-full animate-pulse border border-slate-800"></span>
                 </button>
             )}
         </div>
      </div>

      {/* === BACKDROP === */}
      {(isLeftOpen || isRightOpen) && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => { setIsLeftOpen(false); setIsRightOpen(false); }}
        />
      )}

      {/* === LEFT SIDEBAR === */}
      <aside className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-[#1e293b] border-r border-slate-800 p-5 flex flex-col h-full overflow-y-auto no-scrollbar transition-transform duration-300 ease-in-out shadow-2xl
          lg:translate-x-0 lg:static lg:block lg:shadow-none
          ${isLeftOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center gap-3 mb-10 px-1 cursor-pointer" onClick={() => navigate('/')}>
            <div className="bg-blue-600 p-1 rounded-xl shadow-lg shadow-blue-600/20">
                <img src="/logo192.png" alt="Logo" className="w-14 h-14 rounded-xl object-contain" />
            </div>
            <span className="text-xl font-black tracking-tight text-white uppercase">Trading Bot</span>
        </div>
        
        <nav className="space-y-1.5 mb-8 shrink-0">
          <button onClick={() => setActiveTab('dashboard')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'dashboard' ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
            <LayoutDashboard size={18} /> {t('platform.nav.dashboard')}
          </button>
          <button onClick={() => setActiveTab('history')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'history' ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
            <History size={18} /> {t('platform.nav.history')}
          </button>
          <button onClick={() => setActiveTab('profile')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'profile' ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
            <div className="flex items-center gap-3">
              <Settings size={18} /> {t('platform.nav.profile')}
            </div>
            {userData?.telegramChatId && (
               <span className="w-2 h-2 bg-green-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
            )}
          </button>
          <button onClick={() => setActiveTab('billing')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'billing' ? 'bg-purple-600/10 text-purple-400 border border-purple-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
             <div className="flex items-center gap-3">
               <CreditCard size={18} /> {t('platform.nav.billing')}
             </div>
             {userData?.plan === 'FREE' && (
                <span className="text-[10px] bg-slate-700 px-1.5 py-0.5 rounded text-slate-300">FREE</span>
             )}
          </button>
          
          <div className="pt-4 pb-2">
              <p className="text-[10px] uppercase text-slate-600 font-bold px-4 tracking-wider">{t('platform.nav.system')}</p>
          </div>

          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'settings' ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
            <Zap size={18} /> {t('platform.nav.settings')}
          </button>
          <button onClick={() => setActiveTab('guide')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'guide' ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
            <Book size={18} /> {t('platform.nav.guide')}
          </button>
          
          {isAdmin && (
            <button onClick={() => setActiveTab('admin')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-sm ${activeTab === 'admin' ? 'bg-red-600/10 text-red-400 border border-red-600/20' : 'hover:bg-slate-800 text-slate-400'}`}>
                <ShieldAlert size={18} /> {t('platform.nav.admin')}
            </button>
          )}
        </nav>

        {/* RISK PARAMETERS */}
        <div className="space-y-4 mb-6">
            <div className="bg-[#151e2e] rounded-2xl p-4 border border-slate-700/50 shadow-lg">
                <h3 className="text-xs font-bold text-slate-300 uppercase mb-4 flex items-center gap-2 border-b border-slate-700/50 pb-2">
                   <Settings size={14} className="text-blue-500"/> {t('platform.risk.title')}
                </h3>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-[#0f172a] p-3 rounded-xl border border-slate-800 text-center">
                       <div className="text-[9px] text-slate-500 font-bold uppercase mb-1">{t('platform.risk.risk')}</div>
                       <div className="text-xl font-black text-white tracking-tight">{trading.riskSettings.riskPercent}%</div>
                    </div>
                    <div className="bg-[#0f172a] p-3 rounded-xl border border-slate-800 text-center">
                       <div className="text-[9px] text-slate-500 font-bold uppercase mb-1 flex items-center justify-center gap-1"><BarChart3 size={10}/> {t('platform.risk.leverage')}</div>
                       <div className="text-xl font-black text-yellow-500 tracking-tight">x{trading.riskSettings.leverage}</div>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex justify-between items-center bg-[#0f172a] px-3 py-2.5 rounded-lg border border-slate-800/50">
                        <span className="text-[11px] text-slate-400 font-medium underline decoration-green-500/30">{t('platform.risk.tp')}</span>
                        <span className="text-sm font-mono font-bold text-green-400">{trading.riskSettings.takeProfit}%</span>
                    </div>
                    <div className="flex justify-between items-center bg-[#0f172a] px-3 py-2.5 rounded-lg border border-slate-800/50">
                        <span className="text-[11px] text-slate-400 font-medium underline decoration-red-500/30">{t('platform.risk.sl')}</span>
                        <span className="text-sm font-mono font-bold text-red-400">{trading.riskSettings.stopLoss}%</span>
                    </div>
                </div>
            </div>

            <div className={`rounded-2xl p-4 border shadow-lg transition-colors ${trading.btcStatus.isSafe ? 'bg-green-900/10 border-green-500/20' : 'bg-red-900/10 border-red-500/20'}`}>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('platform.btc.title')}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${trading.btcStatus.isSafe ? 'bg-green-500 text-black' : 'bg-red-500 text-white'}`}>
                        {trading.btcStatus.isSafe ? t('platform.btc.safe') : t('platform.btc.risk')}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-2xl font-black text-white">BTC</span>
                    <span className={`text-xl font-bold ${trading.btcStatus.btcChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {trading.btcStatus.btcChange > 0 ? '+' : ''}{trading.btcStatus.btcChange}%
                    </span>
                </div>
            </div>
        </div>

        <div className="mt-auto space-y-3 pb-2 pt-4 border-t border-slate-800/50">
          <button onClick={togglePaperMode} className={`w-full py-3 rounded-xl text-[11px] font-bold uppercase tracking-wider border transition-all flex items-center justify-center gap-2 ${trading.isPaperMode ? 'bg-blue-600/10 text-blue-400 border-blue-600/30 hover:bg-blue-600/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20'}`}>
            <Shield size={16} />
            {trading.isPaperMode ? t('platform.actions.sim') : t('platform.actions.real')}
          </button>
          
          <div className="grid grid-cols-1 gap-2">
              <button onClick={toggleBot} className={`w-full py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wider transition-all shadow-lg ${trading.isBotRunning ? 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20' : 'bg-green-600 text-white border border-green-500 hover:bg-green-500'}`}>
                 <Power size={16} /> {trading.isBotRunning ? t('platform.actions.stop') : t('platform.actions.start')}
              </button>
              <button onClick={handlePanic} className="w-full py-3 bg-[#1e293b] hover:bg-red-900/20 text-red-500 border border-red-900/30 hover:border-red-500/50 rounded-xl text-[10px] font-bold uppercase flex items-center justify-center gap-2 transition-all">
                 <AlertOctagon size={16} /> {t('platform.actions.panic')}
              </button>
          </div>

          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-slate-500 hover:text-white text-sm font-medium transition-colors hover:bg-slate-800 rounded-lg">
            <LogOut size={16} /> {t('platform.nav.logout')}
          </button>
        </div>
      </aside>

      {/* === MAIN CONTENT === */}
      <main className="flex-1 p-4 lg:p-8 overflow-y-auto h-[calc(100vh-64px)] lg:h-screen no-scrollbar w-full relative bg-[#0f172a] flex flex-col">
        
        {/* DESKTOP HEADER */}
        <header className="hidden lg:flex justify-between items-center mb-6 pb-4 border-b border-slate-800/50 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center gap-2 text-sm text-slate-400">
               <span className="text-slate-500">Platform</span>
               <span className="text-slate-600">/</span>
               <span className="text-white font-medium capitalize">{t(`platform.nav.${activeTab}`) || activeTab}</span>
            </div>
            
            <div></div>
        </header>

        {/* PAGE CONTENT */}
        <div className="flex-1">
            {activeTab === 'dashboard' && (
              <Dashboard 
                selectedSymbol={selectedSymbol} 
                stats={trading.stats}
                balance={trading.balance}
                isBotRunning={trading.isBotRunning}
                toggleBot={toggleBot}
                btcStatus={trading.btcStatus}
                chartData={trading.weeklyStats} 
                userPlan={userData?.plan || 'FREE'}
                strategyMode={strategyMode} 
                setStrategyMode={handleStrategyChange} 
                
                // ✅ ИСПРАВЛЕНИЕ ОШИБКИ e.match
                logs={trading.logs.map((item: any, i) => {
                    // Вариант 1: Если это объект (пришел с Бэкенда через API)
                    if (typeof item === 'object' && item !== null) {
                        return {
                            id: item.id || i,
                            time: item.time || new Date().toLocaleTimeString(),
                            message: item.message || '',
                            type: item.type || 'INFO'
                        };
                    }
                    
                    // Вариант 2: Если это строка (старый формат)
                    const str = String(item);
                    return { 
                        id: i, 
                        time: str.match(/^\[(.*?)\]/)?.[1] || '', 
                        message: str.replace(/^\[.*?\] /, ''), 
                        type: 'INFO' 
                    };
                })} 
              />
            )}
            {activeTab === 'settings' && <ApiSettings />}
            {activeTab === 'profile' && <ProfilePage />} 
            {activeTab === 'billing' && <SubscriptionPanel userData={userData} />}
            {activeTab === 'history' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <h2 className="text-3xl font-black text-white tracking-tight uppercase">{t('platform.nav.history')}</h2>
                <TradeHistoryTable trades={trading.history} onRefresh={trading.fetchAllData} addLog={trading.addLog} />
              </div>
            )}
            {activeTab === 'guide' && <UserGuide />}
            {activeTab === 'admin' && isAdmin && <AdminPanel />}
        </div>

        {/* FOOTER */}
        <footer className="mt-12 py-6 border-t border-slate-800/50 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-2">
                <span className="font-bold">TradingBot Pro</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px]">v1.2.5</span>
                <span>© 2026</span>
            </div>
            <div className="flex items-center gap-6">
                <span className="hover:text-white transition-colors cursor-pointer">{t('platform.footer.docs')}</span>
                <a href="https://t.me/pro_tradings_support_bot" target="_blank" rel="noreferrer" className="hover:text-blue-400 transition-colors flex items-center gap-1">
                   {t('platform.actions.support')}
                </a>
                <span className="hover:text-white transition-colors flex items-center gap-1">
                    {t('platform.footer.made')} <Heart size={10} className="text-red-500 fill-red-500" /> for Traders
                </span>
            </div>
        </footer>
      </main>

      {/* === RIGHT SIDEBAR === */}
      {activeTab === 'dashboard' && (
        <div className={`
            fixed inset-y-0 right-0 z-50 w-96 bg-[#0f172a]/95 backdrop-blur-xl border-l border-slate-800 shadow-2xl transition-transform duration-300 ease-in-out
            lg:translate-x-0 lg:static lg:block lg:shadow-none lg:border-l lg:bg-[#0f172a]
            ${isRightOpen ? 'translate-x-0' : 'translate-x-full'}
        `}>
            <div className="lg:hidden flex justify-between items-center p-4 border-b border-slate-700">
                <span className="font-bold text-white flex items-center gap-2">
                    <SlidersHorizontal size={18} className="text-blue-500" /> {t('platform.nav.settings')}
                </span>
                <button onClick={() => setIsRightOpen(false)} className="p-1 text-slate-400 hover:text-white bg-slate-800 rounded-lg">
                    <X size={24}/>
                </button>
            </div>
            <div className="h-full overflow-y-auto no-scrollbar">
                <RightSidebar 
                    coins={trading.coins} 
                    limit={trading.limit}
                    setLimit={trading.setLimit}
                    selectedSymbol={selectedSymbol}
                    setSelectedSymbol={(sym) => { setSelectedSymbol(sym); setIsRightOpen(false); }}
                    riskSettings={trading.riskSettings}
                    balance={trading.balance}
                    isPaperMode={trading.isPaperMode}
                />
            </div>
        </div>
      )}
    </div>
  );
};